/**
 * AddPapersModal (A1) — add more papers to an EXISTING ontology. Pick KB
 * documents; the backend mines them for NEW concepts/relationships (given the
 * current ontology as context, so it dedups) and returns the new nodes/edges,
 * which the editor merges into the draft. Each batch stays within the ~50k token
 * context limit; larger sets are added in successive batches.
 */
import { useState, useEffect, useMemo } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  Checkbox,
  Slider,
  InlineNotification,
  Loading,
  ProgressBar,
} from '@carbon/react';
import { knowledgeBaseApi } from '@/lib/api/knowledge-base.api';
import { ontologyApi } from '@/lib/api/ontology.api';
import { useKnowledgeBases } from '@/lib/query/hooks/useKnowledgeBases';
import type { KnowledgeBase, KBDocument } from '@shared/types/knowledge-base';

const estimateTokens = (chars: number) => Math.ceil(chars / 4);
const MAX_TOKENS = 50_000;

interface ExtendResult {
  nodes: Array<{ id: string; label: string; definition: string; properties: any[]; position: { x: number; y: number } }>;
  edges: Array<{ id: string; source: string; target: string; label: string; relationshipType: string }>;
}

interface AddPapersModalProps {
  open: boolean;
  onClose: () => void;
  ontologyId: string;
  /** Called with the new (deduped) nodes/edges to merge into the canvas. */
  onExtended: (result: ExtendResult) => void;
}

export function AddPapersModal({ open, onClose, ontologyId, onExtended }: AddPapersModalProps) {
  const { data: knowledgeBases, isLoading: kbLoading } = useKnowledgeBases();

  const [selectedKB, setSelectedKB] = useState<KnowledgeBase | null>(null);
  const [documents, setDocuments] = useState<KBDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [nodeCount, setNodeCount] = useState(15);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSelectedKB(null);
      setDocuments([]);
      setSelectedDocIds(new Set());
      setNodeCount(15);
      setError(null);
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (!selectedKB) {
      setDocuments([]);
      setSelectedDocIds(new Set());
      return;
    }
    let cancelled = false;
    setDocsLoading(true);
    setError(null);
    knowledgeBaseApi
      .listDocuments(selectedKB.id)
      .then((docs) => {
        if (!cancelled) {
          setDocuments(docs);
          setDocsLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load documents');
          setDocsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedKB]);

  const { totalTokens, overBudget } = useMemo(() => {
    let total = 0;
    for (const doc of documents) {
      if (selectedDocIds.has(doc.id)) total += estimateTokens(doc.text_char_count ?? doc.file_size);
    }
    return { totalTokens: total, overBudget: total > MAX_TOKENS };
  }, [documents, selectedDocIds]);

  const completedDocs = documents.filter((d) => d.conversion_status === 'completed');
  const canAdd = selectedDocIds.size > 0 && !overBudget && !busy;

  const toggleDoc = (docId: string) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const handleAdd = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await ontologyApi.extendFromDocuments(ontologyId, Array.from(selectedDocIds), nodeCount);
      onExtended({ nodes: result.nodes, edges: result.edges });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to add papers to the ontology');
      setBusy(false);
    }
  };

  return (
    <ComposedModal open={open} onClose={onClose} size="lg">
      <ModalHeader title="Add papers to this ontology" label="Extend with new documents" />
      <ModalBody>
        {busy && (
          <div style={{ marginBottom: '1rem' }}>
            <ProgressBar label="Mining new concepts from the selected documents..." status="active" />
          </div>
        )}
        {error && (
          <InlineNotification kind="error" title="Error" subtitle={error} lowContrast style={{ marginBottom: '1rem' }} />
        )}

        <div style={{ marginBottom: '1.5rem' }}>
          <Dropdown
            id="addpapers-kb-select"
            titleText="Knowledge Base"
            label={kbLoading ? 'Loading...' : 'Select a knowledge base'}
            items={knowledgeBases || []}
            itemToString={(item: KnowledgeBase | null) => item?.name || ''}
            onChange={({ selectedItem }: { selectedItem: KnowledgeBase | null }) => setSelectedKB(selectedItem)}
            disabled={busy || kbLoading}
          />
        </div>

        {selectedKB && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h5 style={{ marginBottom: '0.5rem' }}>
              Select documents to add
              {completedDocs.length > 0 && (
                <span style={{ fontWeight: 'normal', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                  ({completedDocs.length} available)
                </span>
              )}
            </h5>
            {docsLoading && <Loading small withOverlay={false} description="Loading documents..." />}
            {!docsLoading && completedDocs.length === 0 && (
              <InlineNotification
                kind="info"
                title="No converted documents"
                subtitle="Upload and convert documents in your Knowledge Base first."
                lowContrast
                hideCloseButton
              />
            )}
            {!docsLoading && completedDocs.length > 0 && (
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--cds-border-subtle)', borderRadius: '4px', padding: '0.5rem' }}>
                {completedDocs.map((doc) => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.25rem 0' }}>
                    <Checkbox
                      id={`addpapers-doc-${doc.id}`}
                      labelText={doc.filename}
                      checked={selectedDocIds.has(doc.id)}
                      onChange={() => toggleDoc(doc.id)}
                      disabled={busy}
                    />
                    <span style={{ fontSize: '0.75rem', color: 'var(--cds-text-secondary)', whiteSpace: 'nowrap', marginLeft: '0.5rem' }}>
                      ~{estimateTokens(doc.text_char_count ?? doc.file_size).toLocaleString()} tokens
                    </span>
                  </div>
                ))}
              </div>
            )}
            {selectedDocIds.size > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                  <span>Estimated tokens: <strong>{totalTokens.toLocaleString()}</strong></span>
                  <span style={{ color: overBudget ? 'var(--cds-support-error)' : 'var(--cds-text-secondary)' }}>
                    Batch limit: {MAX_TOKENS.toLocaleString()}
                  </span>
                </div>
                {overBudget && (
                  <InlineNotification
                    kind="error"
                    title="Too many tokens for one batch"
                    subtitle="Deselect some documents; you can add the rest in a second batch."
                    lowContrast
                    hideCloseButton
                    style={{ marginTop: '0.5rem' }}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {selectedKB && completedDocs.length > 0 && (
          <div style={{ marginBottom: '1rem' }}>
            <Slider
              id="addpapers-node-count"
              labelText="Max new concepts to add"
              min={5}
              max={50}
              step={5}
              value={nodeCount}
              onChange={({ value }: { value: number }) => setNodeCount(value)}
              disabled={busy}
            />
          </div>
        )}
      </ModalBody>
      <ModalFooter
        primaryButtonText={busy ? 'Adding...' : 'Add papers'}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleAdd}
        onRequestClose={onClose}
        primaryButtonDisabled={!canAdd}
      >
        <></>
      </ModalFooter>
    </ComposedModal>
  );
}

// Made with Bob
